import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import {
    isFetchingOrganizationDetailAtom,
    isProcessingOrganizationsAtom,
    organizationDetailAtom,
} from "@/store/organizationAtoms";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { Alert } from "@common/Alert";
import { Button, ErrorState, Icon, Spinner, Title } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import {
    ORGANIZATION_USER_ROLE_LABEL,
    type Location,
    type OrganizationDetail,
    type OrganizationStatus,
} from "@models/organization";
import { asApiFailure } from "@utils/apiFailure";
import { isSameGuid } from "@utils/sameGuid";
import { useAtomValue } from "jotai";
import {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { AddLocationDrawer } from "./_components/AddLocationDrawer";
import { AddLocationOutcome } from "./_components/AddLocationOutcome";
import { ApproveLocations } from "./_components/ApproveLocations";
import { DetailField } from "./_components/DetailField";
import { DetailSection } from "./_components/DetailSection";
import {
    EditLocationDrawer,
    type EditLocationSaved,
} from "./_components/EditLocationDrawer";
import {
    EditLocationOutcome,
    type LocationEditOutcome,
} from "./_components/EditLocationOutcome";
import {
    EditOrganizationDrawer,
    type EditOrganizationSaved,
} from "./_components/EditOrganizationDrawer";
import { EditOrganizationOutcome } from "./_components/EditOrganizationOutcome";
import { LocationRow } from "./_components/LocationRow";
import { ResendInvitation } from "./_components/ResendInvitation";
import { StatusBadge } from "./_components/StatusBadge";
import { StatusHistory } from "./_components/StatusHistory";
import { StatusTransitionDialog } from "./_components/StatusTransitionDialog";
import { ALLOWED_ACTIONS, type StatusAction } from "./_components/status";
import {
    addLocationBlockedReason,
    type AddLocationDone,
    type AddLocationOutcomeState,
} from "./_helpers/addLocation";
import { downloadErrorMessage } from "./_helpers/certReview";
import {
    CLIENT_GONE_MESSAGE,
    statusChangeErrorMessage,
} from "./_helpers/statusChange";
import { useFileDownload } from "./_helpers/useFileDownload";

export const ClientDetail = () => {
    const { id = "" } = useParams<{ id: string }>();
    const {
        getOrganization,
        updateOrganizationStatus,
        downloadOrganizationFile,
    } = useOrganizationApi();

    // The detail atom is app-scoped and outlives a move to another client
    // until that client's read lands: only THIS client's detail is shown, so
    // client B's page never paints client A's (#407). Compared as GUIDs: an
    // id pasted in upper case is the same client.
    const detail = useAtomValue(organizationDetailAtom);
    const organization = detail && isSameGuid(detail.id, id) ? detail : null;
    const isFetching = useAtomValue(isFetchingOrganizationDetailAtom);
    const isProcessing = useAtomValue(isProcessingOrganizationsAtom);

    // The action whose confirmation dialog is open, the status the admin
    // decided on when opening it, and the client it was opened for (null =
    // closed).
    const [pending, setPending] = useState<{
        action: StatusAction;
        openedOn: OrganizationStatus;
        clientId: string;
    } | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    // A 409: the page was out of date when the admin decided, so this dialog
    // may not confirm again (review finding 1).
    const [isConflicted, setIsConflicted] = useState(false);
    // The reload after a failure is in flight; the dialog holds until it
    // lands, and only then says what happened (review finding 6).
    const [isReloading, setIsReloading] = useState(false);
    // The client a status change was told no longer exists (404).
    const [goneId, setGoneId] = useState<string | null>(null);
    // Two clicks in one tick must send one status change.
    const runGuarded = useSubmitGuard();
    // The "Company History" drawer.
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    // The "Edit Organization" drawer (C5), and what its last save did.
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editOutcome, setEditOutcome] =
        useState<EditOrganizationSaved | null>(null);
    // After a save, focus goes to its outcome: Edit is held while the page
    // re-reads, so focus cannot go back to it. A passive effect, so it runs
    // after the drawer's unmount cleanup (which would otherwise hand focus to
    // Edit or drop it, depending on whether the re-read had landed). Keyed on
    // the save, so the re-read landing (or failing) does not pull focus back.
    const editOutcomeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (editOutcome) {
            editOutcomeRef.current?.focus();
        }
    }, [editOutcome]);
    // The "Edit Location" drawer (C6): the location it opened on, and what its
    // last save did. Focus goes to that outcome the way it does for Edit
    // Organization's, for the same reasons.
    const [editingLocation, setEditingLocation] = useState<Location | null>(
        null,
    );
    const [locationOutcome, setLocationOutcome] =
        useState<LocationEditOutcome | null>(null);
    const locationOutcomeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (locationOutcome) {
            locationOutcomeRef.current?.focus();
        }
    }, [locationOutcome]);
    // The "New Location" drawer (C4), and what its last add did. Focus goes to
    // that outcome as it does for the edit drawers'.
    const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
    const [addLocationOutcome, setAddLocationOutcome] =
        useState<AddLocationOutcomeState | null>(null);
    const addLocationOutcomeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (addLocationOutcome) {
            addLocationOutcomeRef.current?.focus();
        }
    }, [addLocationOutcome]);
    const addLocationReasonId = useId();

    // A dialog belongs to the client it was opened on (PR 369): the route
    // reuses this page for another client, and a status change confirmed
    // there would act on the other client. So the dialog closes, its error
    // and in-flight state cleared, in the render that sees the new id. The
    // history, edit and New Location drawers close with it, and the last
    // outcomes go: the next client's page opens closed.
    const [dialogClientId, setDialogClientId] = useState(id);
    if (!isSameGuid(dialogClientId, id)) {
        setDialogClientId(id);
        setPending(null);
        setStatusError(null);
        setIsConflicted(false);
        setIsReloading(false);
        setIsHistoryOpen(false);
        setIsEditOpen(false);
        setEditOutcome(null);
        setEditingLocation(null);
        setLocationOutcome(null);
        setIsAddLocationOpen(false);
        setAddLocationOutcome(null);
    }
    // The page's client right now, for a handler that resumes after an await:
    // a status change for a client the page has left does nothing more.
    const routeClientId = useRef(id);
    useLayoutEffect(() => {
        routeClientId.current = id;
    }, [id]);
    const isStillOn = (clientId: string) =>
        isSameGuid(clientId, routeClientId.current);

    // #428: an organization file downloads through the API (the container is
    // private). The org id is the LOADED organization's, whose files are the
    // ones listed.
    const organizationId = organization?.id ?? "";
    const {
        downloadingId,
        error: downloadError,
        start: startDownload,
    } = useFileDownload(
        organizationId,
        (fileId) => downloadOrganizationFile(organizationId, fileId),
        (error) => downloadErrorMessage(error, "this file"),
    );

    // The HTTP status of the newest read's failure (undefined while it is out,
    // and after it succeeded), for a handler that must tell a client that is
    // gone (404) from a read that failed. Only the newest read writes it.
    const readFailureStatus = useRef<number | undefined>(undefined);
    const readDetail = useCallback(
        (isCurrent: () => boolean): Promise<unknown> => {
            if (!id) {
                return Promise.resolve();
            }

            readFailureStatus.current = undefined;
            const read = getOrganization(id);
            // A side branch, so the read itself settles exactly as before (the
            // page's held Edit is timed on it). Registered first, it records
            // the failure before useMountRead settles the read.
            read.catch((error: unknown) => {
                if (isCurrent()) {
                    readFailureStatus.current = asApiFailure(error)?.status;
                }
            });
            return read;
        },
        [id, getOrganization],
    );

    // Issued on mount and for every client the route moves to. The page shows
    // a failure only for its own read of THIS client: a failure loading
    // client A must not paint on client B (#407). `fetchDetail` resolves
    // whether the client loaded (the status-change dialog words its message
    // on it); it never rejects.
    const { readStatus, reload: fetchDetail, runSideRead } =
        useMountRead(readDetail);

    // A save the API accepted. The drawer closes and the page re-reads the
    // client through its own read: the save may have done more than the
    // form shows (a new signup link, the API's normalised email), so the page
    // is not patched from the form. A failed re-read (useApi toasted it)
    // leaves the old copy on screen, and the line by Edit says so.
    const handleEditSaved = (
        clientId: string,
        saved: EditOrganizationSaved,
    ) => {
        if (!isStillOn(clientId)) {
            return;
        }
        setIsEditOpen(false);
        setEditOutcome(saved);
        void fetchDetail();
    };

    // The save's 404 from the API: the client is gone. The page says so the
    // way a status change's 404 does (handleConfirm), with nothing re-read.
    const handleEditGone = (clientId: string) => {
        if (!isStillOn(clientId)) {
            return;
        }
        setIsEditOpen(false);
        setEditOutcome(null);
        setGoneId(clientId);
    };

    // A location save the API accepted. As handleEditSaved: the drawer
    // closes and the page re-reads the client (a change of state moved the
    // location's status, and the save added to its address history).
    const handleLocationSaved = (
        clientId: string,
        locationId: string,
        saved: EditLocationSaved,
    ) => {
        if (!isStillOn(clientId)) {
            return;
        }
        setEditingLocation(null);
        setLocationOutcome({
            kind: "saved",
            locationId,
            sentBackToReview: saved.sentBackToReview,
        });
        void fetchDetail();
    };

    // The API's 404 for a location: the location is gone, or its client is.
    // The page says so and re-reads the client, which takes the row away. A
    // re-read that 404s too says the client is gone, and the page says that
    // instead, as it does for a status change's 404.
    const handleLocationGone = async (clientId: string, locationId: string) => {
        if (!isStillOn(clientId)) {
            return;
        }
        setEditingLocation(null);
        setLocationOutcome({ kind: "gone", locationId });
        const reloaded = await fetchDetail();
        if (
            !reloaded &&
            isStillOn(clientId) &&
            readFailureStatus.current === 404
        ) {
            setLocationOutcome(null);
            setGoneId(clientId);
        }
    };

    // A reject or an approve changed the client's locations: the Locations
    // section's lines (the last location edit's, the last add's) described
    // them before it, so they go, and the page re-reads the client.
    const handleLocationsChanged = () => {
        setLocationOutcome(null);
        setAddLocationOutcome(null);
        void fetchDetail();
    };

    // What the New Location drawer could not keep to itself. The client gone
    // is the page's client-gone view, as an edit's 404 is. Otherwise the
    // drawer closes, the outcome goes above the list, and the page re-reads
    // the client: the new location, a status that now refuses one, or (after a
    // failure that may have added it) whether it is there.
    const handleAddLocationDone = (clientId: string, done: AddLocationDone) => {
        if (!isStillOn(clientId)) {
            return;
        }
        setIsAddLocationOpen(false);
        if (done.kind === "gone") {
            setAddLocationOutcome(null);
            setGoneId(clientId);
            return;
        }
        setAddLocationOutcome(done);
        void fetchDetail();
    };

    // "Load latest" after organization_changed: the drawer needs the detail
    // itself to refill its form, so this is a side read of the page's own
    // read, which publishes the same detail to the page.
    const loadLatestDetail = async (): Promise<OrganizationDetail | null> => {
        let latest: OrganizationDetail | null = null;
        await runSideRead(async (isCurrent) => {
            const detail = await getOrganization(id);
            if (isCurrent()) {
                latest = detail;
            }
        });
        return latest;
    };

    // `reason` is undefined when none was given, which leaves it out of the
    // request body. `opened` is the dialog as it was opened: a confirm for a
    // client the page no longer shows posts nothing, and a request that
    // answers after the page has moved on changes nothing on it.
    const handleConfirm = (
        opened: { action: StatusAction; clientId: string },
        reason: string | undefined,
    ) =>
        runGuarded(async () => {
            if (!isStillOn(opened.clientId)) {
                return;
            }
            try {
                await updateOrganizationStatus(opened.clientId, {
                    status: opened.action.target,
                    reason,
                });
            } catch (error) {
                if (!isStillOn(opened.clientId)) {
                    return;
                }
                const status = asApiFailure(error)?.status;

                // Deleted (review finding 3): nothing to reload, whose own
                // read could 404 and toast, and nothing left to act on.
                if (status === 404) {
                    setPending(null);
                    setGoneId(opened.clientId);
                    return;
                }

                // The dialog stays open, held while the page reloads behind
                // it, so its message can say whether the page is current.
                setIsReloading(true);
                // False when it failed: useApi toasted it; the loaded page stays.
                const reloaded = await fetchDetail();
                if (!isStillOn(opened.clientId)) {
                    return;
                }
                setIsReloading(false);
                setIsConflicted(status === 409);
                setStatusError(statusChangeErrorMessage(error, reloaded));
                return;
            }

            if (!isStillOn(opened.clientId)) {
                return;
            }
            setPending(null);
            // The last edits' and add's lines described the client before
            // this change.
            setEditOutcome(null);
            setLocationOutcome(null);
            setAddLocationOutcome(null);
            void fetchDetail();
        });

    // A failed REFETCH keeps the loaded page (useApi toasted it): replacing a
    // client's detail with a bare ErrorState would unmount a row's freshly
    // rendered "Certificate acknowledged" for a read that changed nothing.
    if (readStatus === "failed" && !organization) {
        return (
            <div className="px-10">
                <ErrorState onRetry={fetchDetail} />
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="flex justify-center px-10 py-20">
                <Spinner />
                <span className="sr-only">
                    {isFetching ? "Loading client" : "No client"}
                </span>
            </div>
        );
    }

    if (goneId !== null && isSameGuid(goneId, organization.id)) {
        return (
            <div className="px-10 pb-12 text-neutral">
                <Title>{organization.name}</Title>
                <Alert color={ComponentColor.Error} className="mt-4">
                    {CLIENT_GONE_MESSAGE}
                </Alert>
            </div>
        );
    }

    const actions = ALLOWED_ACTIONS[organization.status] ?? [];
    // Why this client can't take a new location (the API refuses it), or null.
    const addLocationBlocked = addLocationBlockedReason(organization.status);
    // Review finding 1: the dialog confirms only what the admin decided on
    // the status the page still shows. A 409 says the page was already out of
    // date; a reload that moved the status (a 409 or not) means the action was
    // chosen for a status the client no longer has. Either way the admin backs
    // out and decides again. This also covers the action no longer being
    // allowed: it was taken from ALLOWED_ACTIONS[openedOn].
    const canConfirm =
        pending !== null &&
        !isConflicted &&
        organization.status === pending.openedOn;

    return (
        <div className="px-10 pb-12 text-neutral">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Title>{organization.name}</Title>
                    <StatusBadge status={organization.status} />
                </div>
                <div className="flex flex-wrap gap-2">
                    {actions.map((action) => (
                        <Button
                            key={action.key}
                            color={action.color}
                            size={ComponentSize.SM}
                            disabled={isProcessing}
                            onClick={() => {
                                setStatusError(null);
                                setIsConflicted(false);
                                setPending({
                                    action,
                                    openedOn: organization.status,
                                    clientId: id,
                                });
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <DetailSection title="Organization">
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <DetailField label="Org Name">
                            {organization.name}
                        </DetailField>
                        <DetailField label="Address">
                            {[
                                organization.streetAddress,
                                organization.city,
                                organization.state,
                                organization.zipCode,
                                organization.country,
                            ]
                                .filter(Boolean)
                                .join(", ")}
                        </DetailField>
                        <DetailField label="Contact">
                            {[organization.firstName, organization.lastName]
                                .filter(Boolean)
                                .join(" ")}
                        </DetailField>
                        <DetailField label="Email">
                            {organization.email}
                        </DetailField>
                        <DetailField label="Phone">
                            {organization.phoneNumber}
                        </DetailField>
                    </dl>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {/* Held until the page's newest read of the client
                            has succeeded: while a re-read is out, and after
                            one failed, the drawer would open on the copy a
                            save just replaced, whose updatedAt the API
                            refuses. */}
                        <Button
                            color={ComponentColor.Primary}
                            outline
                            size={ComponentSize.SM}
                            disabled={isProcessing || readStatus !== "loaded"}
                            onClick={() => {
                                setEditOutcome(null);
                                setIsEditOpen(true);
                            }}
                        >
                            <Icon name="edit" />
                            Edit
                        </Button>
                        <Button
                            color={ComponentColor.Primary}
                            outline
                            size={ComponentSize.SM}
                            onClick={() => setIsHistoryOpen(true)}
                        >
                            <Icon name="history" />
                            View History
                        </Button>
                    </div>
                    {/* Why Edit is held, for as long as it is: from the
                        page's newest read, whatever issued it (a save's
                        re-read, a status change's reload), so only a read
                        that succeeds clears it. useApi's toast fades. */}
                    {readStatus === "failed" && (
                        <p role="alert" className="mt-3 text-sm text-error">
                            Couldn&apos;t load the latest details. Reload the
                            page to edit.
                        </p>
                    )}
                    {editOutcome && (
                        <div
                            ref={editOutcomeRef}
                            tabIndex={-1}
                            className="mt-3 text-sm focus:outline-none"
                        >
                            <EditOrganizationOutcome saved={editOutcome} />
                        </div>
                    )}
                </DetailSection>

                <DetailSection title="Legal & Tax">
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <DetailField label="Legal Business Name">
                            {organization.legalBusinessName}
                        </DetailField>
                        <DetailField label="Tax ID">
                            {organization.taxId}
                        </DetailField>
                    </dl>
                </DetailSection>

                <DetailSection title="Locations">
                    {addLocationOutcome && (
                        <div
                            ref={addLocationOutcomeRef}
                            tabIndex={-1}
                            className="mb-3 text-sm focus:outline-none"
                        >
                            <AddLocationOutcome outcome={addLocationOutcome} />
                        </div>
                    )}
                    {locationOutcome?.kind === "gone" && (
                        <div
                            ref={locationOutcomeRef}
                            tabIndex={-1}
                            className="mb-3 text-sm focus:outline-none"
                        >
                            <EditLocationOutcome outcome={locationOutcome} />
                        </div>
                    )}
                    {organization.locations.length === 0 ? (
                        <p className="text-sm text-neutral/70">
                            No locations on file.
                        </p>
                    ) : (
                        <ul className="divide-y divide-base-300">
                            {organization.locations.map((location) => (
                                <LocationRow
                                    key={location.id}
                                    location={location}
                                    onRejected={handleLocationsChanged}
                                    onAcknowledged={fetchDetail}
                                    // Held as the organization's Edit is.
                                    isEditHeld={
                                        isProcessing || readStatus !== "loaded"
                                    }
                                    onEdit={() => {
                                        setLocationOutcome(null);
                                        setAddLocationOutcome(null);
                                        setEditingLocation(location);
                                    }}
                                    outcome={
                                        locationOutcome?.kind === "saved" &&
                                        isSameGuid(
                                            locationOutcome.locationId,
                                            location.id,
                                        ) && (
                                            <div
                                                ref={locationOutcomeRef}
                                                tabIndex={-1}
                                                className="mt-1 text-sm focus:outline-none"
                                            >
                                                <EditLocationOutcome
                                                    outcome={locationOutcome}
                                                />
                                            </div>
                                        )
                                    }
                                />
                            ))}
                        </ul>
                    )}
                    {/* C4: the admin adds a location and approves it in one
                        step. Held while a write is out; for a client the API
                        refuses (suspended, cancelled) it says why. */}
                    <div className="mt-4">
                        <Button
                            color={ComponentColor.Primary}
                            size={ComponentSize.SM}
                            disabled={addLocationBlocked !== null || isProcessing}
                            aria-describedby={
                                addLocationBlocked !== null
                                    ? addLocationReasonId
                                    : undefined
                            }
                            onClick={() => {
                                setLocationOutcome(null);
                                setAddLocationOutcome(null);
                                setIsAddLocationOpen(true);
                            }}
                        >
                            <Icon name="add" />
                            New Location
                        </Button>
                        {addLocationBlocked !== null && (
                            <p
                                id={addLocationReasonId}
                                className="mt-1 text-sm text-neutral/70"
                            >
                                {addLocationBlocked}
                            </p>
                        )}
                    </div>
                </DetailSection>

                {/* E5-S9 approve: charges + approves ALL this org's PENDING
                    locations at once (the endpoint has no per-location scope),
                    so it stays an org-level action rather than a per-row button. */}
                <DetailSection title="Approve pending locations">
                    <ApproveLocations
                        organization={organization}
                        onApproved={handleLocationsChanged}
                    />
                </DetailSection>

                <DetailSection title="Org Users">
                    {organization.roles.length === 0 ? (
                        <p className="text-sm text-neutral/70">
                            No users on file.
                        </p>
                    ) : (
                        <ul className="divide-y divide-base-300">
                            {organization.roles.map((member) => (
                                <li
                                    key={member.userId}
                                    className="flex items-center justify-between py-2"
                                >
                                    <span>
                                        <span className="font-medium">
                                            {member.userName}
                                        </span>{" "}
                                        <span className="text-sm text-neutral/70">
                                            {member.userEmail}
                                        </span>
                                    </span>
                                    <span className="text-sm">
                                        {ORGANIZATION_USER_ROLE_LABEL[
                                            member.role
                                        ] ?? "—"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* Keyed by client: the route reuses this page for
                        another client, whose page must not show this one's
                        outcome. */}
                    <ResendInvitation
                        key={organization.id}
                        organization={organization}
                        onChanged={() => {
                            // A failure: useApi toasted it; the loaded page stays.
                            void fetchDetail();
                        }}
                        // The last edit's line may name the link this one
                        // just replaced.
                        onSent={() => setEditOutcome(null)}
                    />
                </DetailSection>

                {/* TODO(post-MVP): should org-level files exist at all beside each location's certificate panel? Awaiting a PO ruling */}
                <DetailSection title="Uploaded Files">
                    {organization.files.length === 0 ? (
                        <p className="text-sm text-neutral/70">
                            No files uploaded.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {organization.files.map((file, index) => {
                                // A row without a name (an older API, a
                                // legacy record) still gets a label, an
                                // accessible name and a save name.
                                const label =
                                    file.fileName || `File ${index + 1}`;
                                const isDownloading = downloadingId === file.id;

                                return (
                                    <li key={file.id}>
                                        <button
                                            type="button"
                                            className="link link-primary inline-flex items-start gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
                                            disabled={downloadingId !== null}
                                            aria-busy={isDownloading}
                                            onClick={() =>
                                                void startDownload({
                                                    id: file.id,
                                                    fileName: label,
                                                })
                                            }
                                        >
                                            {isDownloading ? (
                                                <span className="loading loading-spinner loading-xs" />
                                            ) : (
                                                <Icon name="description" />
                                            )}
                                            <span className="min-w-0 break-all">
                                                {label}
                                            </span>
                                        </button>
                                        {downloadError?.fileId === file.id && (
                                            <p
                                                role="alert"
                                                className="mt-1 text-sm text-error"
                                            >
                                                {downloadError.message}
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </DetailSection>
            </div>

            {isHistoryOpen && (
                <AdminDrawer
                    title="Company History"
                    onClose={() => setIsHistoryOpen(false)}
                >
                    <StatusHistory history={organization.statusHistory} />
                </AdminDrawer>
            )}

            {isEditOpen && (
                <EditOrganizationDrawer
                    organization={organization}
                    onClose={() => setIsEditOpen(false)}
                    onSaved={(saved) =>
                        handleEditSaved(organization.id, saved)
                    }
                    onGone={() => handleEditGone(organization.id)}
                    loadLatest={loadLatestDetail}
                />
            )}

            {editingLocation && (
                <EditLocationDrawer
                    location={editingLocation}
                    onClose={() => setEditingLocation(null)}
                    onSaved={(saved) =>
                        handleLocationSaved(
                            organization.id,
                            editingLocation.id,
                            saved,
                        )
                    }
                    onGone={() =>
                        void handleLocationGone(
                            organization.id,
                            editingLocation.id,
                        )
                    }
                    loadLatest={loadLatestDetail}
                />
            )}

            {isAddLocationOpen && (
                <AddLocationDrawer
                    organizationId={organization.id}
                    onClose={() => setIsAddLocationOpen(false)}
                    onDone={(done) =>
                        handleAddLocationDone(organization.id, done)
                    }
                />
            )}

            {pending && (
                <StatusTransitionDialog
                    action={pending.action}
                    orgName={organization.name}
                    isProcessing={isProcessing || isReloading}
                    errorMessage={statusError}
                    canConfirm={canConfirm}
                    onConfirm={(reason) =>
                        void handleConfirm(pending, reason)
                    }
                    onClose={() => setPending(null)}
                />
            )}
        </div>
    );
};
